import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { StatusBadge } from '../ui/StatusBadge'
import { EntityHeader } from '../ui/EntityHeader'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'

function TicketIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M15 5v2" /><path d="M15 11v2" /><path d="M15 17v2" />
      <path d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z" />
    </svg>
  )
}

function CalendarIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function UserIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  )
}

interface Ticket {
  id: string
  ticket_number: number
  subject: string
  description: string | null
  status: string
  priority: string
  client_name: string | null
  site_name: string | null
  assigned_name: string | null
  assigned_to: string | null
  sla_breached: number
  first_response_due: string | null
  first_response_at: string | null
  resolution_due: string | null
  resolved_at: string | null
  linked_work_order_id: string | null
  created_at: string
  updated_at: string
}

interface Message {
  id: string
  sender_name: string | null
  message_type: string
  is_internal: number
  body: string
  created_at: string
}

interface User {
  id: string
  full_name: string
  slug: string
}

const STATUSES = ['open', 'pending', 'in_progress', 'waiting_on_client', 'resolved', 'closed']
const PRIORITIES = ['low', 'medium', 'high', 'critical']

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'var(--err)', high: 'var(--warn)', medium: 'var(--accent)', low: 'var(--color-infra)',
}

const PRIORITY_BADGE_VARIANT: Record<string, 'default' | 'ok' | 'warn' | 'err' | 'accent' | 'info'> = {
  critical: 'err', high: 'warn', medium: 'accent', low: 'default',
}

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'ok' | 'warn' | 'err' | 'accent' | 'info'> = {
  open: 'info', pending: 'warn', in_progress: 'accent', waiting_on_client: 'warn', resolved: 'ok', closed: 'default',
}

interface Props {
  id: string
  onBack: () => void
}

export function TicketDetail({ id, onBack }: Props) {
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [replyBody, setReplyBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [sending, setSending] = useState(false)
  const { toast, toastError } = useToast()

  const load = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([
        api.get<Ticket>(`/tickets/${id}`),
        api.get<Message[]>(`/tickets/${id}/messages`),
      ])
      setTicket(t)
      setMessages(m)
    } catch (err) {
      toastError('Failed to load ticket', err)
    } finally {
      setLoading(false)
    }
  }, [id, toastError])

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get<{ data: User[] }>('/users')
      setUsers(res.data || [])
    } catch { /* non-critical */ }
  }, [])

  useEffect(() => { load(); loadUsers() }, [load, loadUsers])

  const sendReply = async () => {
    if (!replyBody.trim()) return
    setSending(true)
    try {
      await api.post(`/tickets/${id}/messages`, {
        body: replyBody,
        is_internal: isInternal,
        message_type: isInternal ? 'note' : 'reply',
      })
      setReplyBody('')
      load()
    } catch (err) {
      toastError('Failed to send message', err)
    } finally {
      setSending(false)
    }
  }

  const updateStatus = async (status: string) => {
    try {
      await api.patch(`/tickets/${id}`, { status })
      toast(`Status updated to ${status}`)
      load()
    } catch (err) {
      toastError('Failed to update status', err)
    }
  }

  const updatePriority = async (priority: string) => {
    try {
      await api.patch(`/tickets/${id}`, { priority })
      toast('Priority updated')
      load()
    } catch (err) {
      toastError('Failed to update priority', err)
    }
  }

  const assignTo = async (userId: string) => {
    try {
      await api.post(`/tickets/${id}/assign`, { assigned_to: userId })
      toast('Ticket assigned')
      load()
    } catch (err) {
      toastError('Failed to assign ticket', err)
    }
  }

  const createDispatch = async () => {
    try {
      const res = await api.post<{ slug: string }>(`/tickets/${id}/dispatch`)
      toast(`Work order ${res.slug} created`)
      load()
    } catch (err) {
      toastError('Failed to create dispatch', err)
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
  if (!ticket) return <div style={{ padding: 40, textAlign: 'center' }}>Ticket not found</div>

  const timeUntil = (dateStr: string | null) => {
    if (!dateStr) return null
    const diff = new Date(dateStr).getTime() - Date.now()
    if (diff < 0) return `${Math.round(-diff / 60000)}m overdue`
    if (diff < 3600000) return `${Math.round(diff / 60000)}m left`
    return `${Math.round(diff / 3600000)}h left`
  }

  return (
    <div className="page-enter">
      <div className="page-header" style={{ marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={onBack}>
          &#x2190; Back to Tickets
        </Button>
      </div>

      <EntityHeader
        icon={TicketIcon}
        title={`#${ticket.ticket_number} ${ticket.subject}`}
        badges={[
          { label: ticket.priority, variant: PRIORITY_BADGE_VARIANT[ticket.priority] || 'default' },
          { label: ticket.status.replace(/_/g, ' '), variant: STATUS_BADGE_VARIANT[ticket.status] || 'default', dot: true },
        ]}
        metrics={[
          { icon: CalendarIcon, label: 'Created', value: new Date(ticket.created_at).toLocaleDateString() },
          { icon: UserIcon, label: 'Assigned to', value: ticket.assigned_name || 'Unassigned' },
        ]}
      />

      {/* SLA warning */}
      {ticket.sla_breached ? (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16,
          background: 'var(--err-m)', border: '1px solid var(--err)', color: 'var(--err)',
          fontSize: 13, fontWeight: 600,
        }}>
          SLA Breached — Response or resolution time exceeded
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Message thread */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {messages.map(m => (
              <div key={m.id} style={{
                padding: '10px 14px', borderRadius: 8,
                background: m.is_internal ? 'var(--accent-m)' : m.message_type === 'system' ? 'var(--bg-inset)' : 'var(--bg-surface)',
                border: m.is_internal ? '1px solid var(--accent-m)' : '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: m.message_type === 'system' ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                    {m.sender_name || 'System'}
                    {m.is_internal ? ' (internal note)' : ''}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', color: m.message_type === 'system' ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                  {m.body}
                </div>
              </div>
            ))}
          </div>

          {/* Reply box */}
          <div style={{ background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)', padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button
                onClick={() => setIsInternal(false)}
                style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  background: !isInternal ? 'var(--primary)' : 'transparent',
                  color: !isInternal ? '#fff' : 'var(--text-secondary)',
                  border: !isInternal ? 'none' : '1px solid var(--border)',
                  fontWeight: 600,
                }}
              >
                Reply
              </button>
              <button
                onClick={() => setIsInternal(true)}
                style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  background: isInternal ? 'var(--accent)' : 'transparent',
                  color: isInternal ? '#fff' : 'var(--text-secondary)',
                  border: isInternal ? 'none' : '1px solid var(--border)',
                  fontWeight: 600,
                }}
              >
                Internal Note
              </button>
            </div>
            <textarea
              className="v3-textarea"
              value={replyBody}
              onChange={e => setReplyBody(e.target.value)}
              placeholder={isInternal ? 'Add an internal note...' : 'Type your reply...'}
              style={{ width: '100%', minHeight: 80, resize: 'vertical', fontSize: 13, marginBottom: 8 }}
            />
            <Button onClick={sendReply} disabled={!replyBody.trim()} loading={sending}>
              {isInternal ? 'Add Note' : 'Send Reply'}
            </Button>
          </div>
        </div>

        {/* Right sidebar */}
        <div style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Client & Site */}
          <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>Details</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Client:</strong> {ticket.client_name || '-'}</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Site:</strong> {ticket.site_name || '-'}</div>
          </div>

          {/* Assigned */}
          <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>Assigned To</div>
            <div className="v3-select-wrap">
              <select className="v3-select" value={ticket.assigned_to || ''} onChange={e => assignTo(e.target.value)} style={{ width: '100%', fontSize: 13 }}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
          </div>

          {/* Status */}
          <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>Status</div>
            <div className="v3-select-wrap">
              <select className="v3-select" value={ticket.status} onChange={e => updateStatus(e.target.value)} style={{ width: '100%', fontSize: 13 }}>
                {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
          </div>

          {/* Priority */}
          <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>Priority</div>
            <div className="v3-select-wrap">
              <select className="v3-select" value={ticket.priority} onChange={e => updatePriority(e.target.value)} style={{ width: '100%', fontSize: 13 }}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
          </div>

          {/* SLA */}
          <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>SLA</div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              <strong>First Response:</strong>{' '}
              {ticket.first_response_at ? (
                <span style={{ color: 'var(--success)' }}>Met</span>
              ) : ticket.first_response_due ? (
                <span style={{ color: new Date(ticket.first_response_due) < new Date() ? 'var(--err)' : 'var(--text-secondary)' }}>
                  {timeUntil(ticket.first_response_due)}
                </span>
              ) : '-'}
            </div>
            <div style={{ fontSize: 12 }}>
              <strong>Resolution:</strong>{' '}
              {ticket.resolved_at ? (
                <span style={{ color: 'var(--success)' }}>Resolved</span>
              ) : ticket.resolution_due ? (
                <span style={{ color: new Date(ticket.resolution_due) < new Date() ? 'var(--err)' : 'var(--text-secondary)' }}>
                  {timeUntil(ticket.resolution_due)}
                </span>
              ) : '-'}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {!ticket.linked_work_order_id && (
              <Button
                onClick={createDispatch}
                style={{ width: '100%', fontSize: 13 }}
              >
                Create Dispatch
              </Button>
            )}
            {ticket.linked_work_order_id && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
                Dispatch linked
              </div>
            )}
            <button
              onClick={() => updateStatus('closed')}
              style={{
                width: '100%', padding: '8px 16px', borderRadius: 6,
                background: 'transparent', border: '1px solid var(--border)',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Close Ticket
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
